import { useState } from "react";
import { View } from "react-native";
import { TurnstileField } from "~/components/TurnstileField";
import { Button } from "~/components/ui/Button";
import { SegmentedControl } from "~/components/ui/SegmentedControl";
import { BodyText } from "~/components/ui/Text";
import { TextField } from "~/components/ui/TextField";
import { useAuth } from "~/hooks/useAuth";
import { useTurnstile } from "~/hooks/useTurnstile";
import { spacing, useTheme } from "~/styles/theme";

type Mode = "signIn" | "signUp" | "reset";

/**
 * Email + password sign-in / create-account form, shared by the Account
 * screen and the web sign-in gate, plus the "forgot password" step that emails
 * a reset link. `signUp` and `signInWithEmailPassword` both set the signed-in
 * user on the auth context themselves, so callers only need `onSuccess` for
 * navigation (the Account modal closes itself; the web gate just re-renders
 * once `user` appears).
 */
export function AuthForm({
  onSuccess,
  initialMode = "signIn",
}: {
  onSuccess?: () => void;
  initialMode?: Mode;
}) {
  const { colors } = useTheme();
  const { signUp, signInWithEmailPassword, requestPasswordReset } = useAuth();
  const [mode, setMode] = useState<Mode>(initialMode);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const turnstile = useTurnstile();

  const switchMode = (next: Mode) => {
    setMode(next);
    setError("");
    setNotice("");
  };

  const submit = async () => {
    if (submitting) return;
    setSubmitting(true);
    setError("");
    setNotice("");
    if (mode === "reset") {
      const result = await requestPasswordReset(email, turnstile.captchaToken);
      setSubmitting(false);
      turnstile.reset();
      if (result.error) setError(result.error);
      else
        setNotice(
          "If an account exists for that email, a reset link is on its way.",
        );
      return;
    }
    const result =
      mode === "signUp"
        ? await signUp(email, password, turnstile.captchaToken)
        : await signInWithEmailPassword(
            email,
            password,
            turnstile.captchaToken,
          );
    setSubmitting(false);
    turnstile.reset();
    if (result.error) setError(result.error);
    else onSuccess?.();
  };

  return (
    <View style={{ gap: spacing.lg }}>
      {mode === "reset" ? (
        <BodyText color={colors.muted}>
          Enter your email address and we’ll send you a link to choose a new
          password.
        </BodyText>
      ) : (
        <SegmentedControl
          value={mode}
          onChange={switchMode}
          options={[
            { value: "signIn", label: "Sign in" },
            { value: "signUp", label: "Create account" },
          ]}
        />
      )}

      <TextField
        label="Email"
        autoCapitalize="none"
        autoComplete="email"
        keyboardType="email-address"
        value={email}
        onChangeText={setEmail}
        onSubmitEditing={mode === "reset" ? () => void submit() : undefined}
      />
      {mode === "reset" ? null : (
        <TextField
          label="Password"
          secureTextEntry
          autoCapitalize="none"
          autoComplete={mode === "signUp" ? "new-password" : "current-password"}
          value={password}
          onChangeText={setPassword}
          onSubmitEditing={() => void submit()}
        />
      )}

      {error ? <BodyText color={colors.danger}>{error}</BodyText> : null}
      {notice ? <BodyText color={colors.muted}>{notice}</BodyText> : null}

      <TurnstileField
        onToken={turnstile.setToken}
        resetKey={turnstile.resetKey}
      />

      <Button
        label={
          submitting
            ? "Please wait…"
            : mode === "reset"
              ? "Send reset link"
              : mode === "signUp"
                ? "Create account"
                : "Sign in"
        }
        disabled={submitting || !turnstile.passed}
        fullWidth
        onPress={() => void submit()}
      />

      {mode === "signIn" ? (
        <Button
          label="Forgot password?"
          variant="ghost"
          fullWidth
          onPress={() => switchMode("reset")}
        />
      ) : null}
      {mode === "reset" ? (
        <Button
          label="Back to sign in"
          variant="ghost"
          fullWidth
          onPress={() => switchMode("signIn")}
        />
      ) : null}
    </View>
  );
}
